import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import LandingPageNavbar from "../LandingPage/LandingPageNavbar";
import LandingPageFooter from "../LandingPage/LandingPageFooter";
import {
    fetchLearningHubArticles,
    fetchLearningHubCategories,
    fetchLearningHubFeatured,
} from "../../rtk/thunks/learningHubThunk";
import { setLearningHubFilters } from "../../rtk/slices/learningHubSlice";
import ArticleFilters from "./ArticleFilters";
import { ArticleCard, FeaturedArticles } from "./ArticleCard";
import "./LearningHub.css";

function LearningHubShell({ children }) {
    return (
        <>
            <LandingPageNavbar />
            {children}
            <LandingPageFooter />
        </>
    );
}

function buildListParams(filters, page) {
    const params = { page };
    if (filters.category) params.category = filters.category;
    if (filters.topic) params.topic = filters.topic;
    if (filters.q?.trim()) params.q = filters.q.trim();
    return params;
}

export default function LearningHub() {
    const dispatch = useDispatch();
    const {
        categories,
        featured,
        articles,
        articlesCount,
        articlesNext,
        listLoading,
        listError,
        filters,
    } = useSelector((state) => state.learningHub);

    const [searchInput, setSearchInput] = useState(filters.q || "");
    const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

    useEffect(() => {
        dispatch(fetchLearningHubCategories());
        dispatch(fetchLearningHubFeatured());
        dispatch(setLearningHubFilters({ page: 1, category: "", topic: "", q: "" }));
        dispatch(fetchLearningHubArticles({ page: 1 }));
    }, [dispatch]);

    const loadPage = (page, nextFilters) => {
        const merged = { ...filters, ...nextFilters, page };
        dispatch(setLearningHubFilters(merged));
        dispatch(fetchLearningHubArticles(buildListParams(merged, page)));
    };

    const applyFilters = (nextFilters) => {
        setMobileFiltersOpen(false);
        loadPage(1, nextFilters);
    };

    const handleSearchSubmit = (event) => {
        event.preventDefault();
        applyFilters({ q: searchInput });
    };

    const handleClearFilters = () => {
        setSearchInput("");
        applyFilters({ category: "", topic: "", q: "" });
    };

    const handleLoadMore = () => {
        if (!articlesNext || listLoading) return;
        loadPage((filters.page || 1) + 1, {});
    };

    const hasActiveFilters = Boolean(filters.category || filters.topic || filters.q);
    const showEmpty = !listLoading && articles.length === 0;

    return (
        <LearningHubShell>
            <main className="lh-page lh-hub-page">
                <header className="lh-header">
                    <p className="lh-eyebrow">CPHQ Learning Hub</p>
                    <h1>CPHQ Articles &amp; Study Guides</h1>
                    <p className="lh-intro">
                        Expert articles and study guides across CPHQ certification prep and all seven
                        healthcare quality domains.
                    </p>
                </header>

                <FeaturedArticles articles={featured} />

                <div className="lh-toolbar">
                    <span className="lh-toolbar-label">Filters</span>
                    <form className="lh-search" onSubmit={handleSearchSubmit}>
                        <label className="lh-sr-only" htmlFor="lh-search-input">
                            Search articles
                        </label>
                        <input
                            id="lh-search-input"
                            type="search"
                            placeholder="Search articles…"
                            value={searchInput}
                            onChange={(event) => setSearchInput(event.target.value)}
                        />
                        <button type="submit">Search</button>
                    </form>
                </div>

                {listError && (
                    <div className="lh-alert" role="alert">
                        {typeof listError === "string"
                            ? listError
                            : listError?.detail || "Could not load articles."}
                    </div>
                )}

                <div className="lh-layout">
                    <ArticleFilters
                        categories={categories}
                        activeCategory={filters.category}
                        activeTopic={filters.topic}
                        onSelectCategory={(slug) => applyFilters({ category: slug, topic: "" })}
                        onSelectTopic={(categorySlug, topicSlug) =>
                            applyFilters({ category: categorySlug, topic: topicSlug })
                        }
                        onClear={handleClearFilters}
                        mobileOpen={mobileFiltersOpen}
                        onToggleMobile={() => setMobileFiltersOpen((open) => !open)}
                    />

                    <div className="lh-main">
                        {hasActiveFilters && (
                            <p className="lh-results-meta">
                                {articlesCount} article{articlesCount === 1 ? "" : "s"} found
                            </p>
                        )}

                        {listLoading && articles.length === 0 && (
                            <p className="lh-muted lh-loading">Loading articles…</p>
                        )}

                        {showEmpty && (
                            <div className="lh-empty">
                                <p>No articles found.</p>
                                {hasActiveFilters && (
                                    <button
                                        type="button"
                                        className="lh-clear-btn"
                                        onClick={handleClearFilters}
                                    >
                                        Clear filters
                                    </button>
                                )}
                            </div>
                        )}

                        {!showEmpty && (
                            <div className="lh-grid">
                                {articles.map((item) => (
                                    <ArticleCard key={item.slug} article={item} />
                                ))}
                            </div>
                        )}

                        {articlesNext && !showEmpty && (
                            <div className="lh-load-more-wrap">
                                <button
                                    type="button"
                                    className="lh-load-more"
                                    onClick={handleLoadMore}
                                    disabled={listLoading}
                                >
                                    {listLoading ? "Loading…" : "Load more"}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </main>
        </LearningHubShell>
    );
}
